import { useEffect, useState } from "react";
import { Check, Loader2 } from "lucide-react";
import type { CatalogEditor, CatalogList, EditorRow } from "./useCatalogEditor";

/**
 * How a row tells the admin its edit reached the server — the replacement for
 * the «Guardar» button that used to be the only proof.
 */

/** The inline slot: a spinner while saving, a ✓ for a moment after. */
export function RowStatusIcon({ status }: { status: EditorRow<CatalogList>["status"] }): React.JSX.Element | null {
  const [showSaved, setShowSaved] = useState(false);

  useEffect(() => {
    if (status !== "saved") return;
    setShowSaved(true);
    const timer = setTimeout(() => setShowSaved(false), 2000);
    return () => clearTimeout(timer);
  }, [status]);

  if (status === "saving") {
    return (
      <Loader2 size={13} className="animate-spin text-ink-300" aria-label="Guardando" role="status" />
    );
  }
  if (status === "saved" && showSaved) {
    return <Check size={14} className="text-moss" aria-label="Guardado" role="status" />;
  }
  return null;
}

/**
 * What goes under a row besides its validation messages: a failed save in the
 * server's own words with «Reintentar» / «Deshacer», or — for a row that does
 * not exist yet — what it still needs before it can be saved.
 *
 * `renderErrorAction` lets a caller answer a specific error code with its own
 * action (the inactive-product «Reactivar»).
 */
export function RowSaveFooter<L extends CatalogList>({
  editor,
  list,
  row,
  renderErrorAction,
}: {
  editor: CatalogEditor;
  list: L;
  row: EditorRow<L>;
  renderErrorAction?: (row: EditorRow<L>) => React.ReactNode;
}): React.JSX.Element | null {
  if (row.status === "error" && row.error) {
    const special = renderErrorAction?.(row);
    return (
      <div role="alert" className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 pb-2 text-[12px]">
        <span className="text-red-600">{row.error}</span>
        {special ?? (
          <>
            <button
              type="button"
              onClick={() => void editor.commit(list, row.key)}
              className="font-semibold text-moss hover:underline"
            >
              Reintentar
            </button>
            <button
              type="button"
              onClick={() => editor.undo(list, row.key)}
              className="font-semibold text-ink-500 hover:underline"
            >
              Deshacer
            </button>
          </>
        )}
      </div>
    );
  }

  if (row.id === null && row.revealed) {
    return (
      <p className="px-4 pb-2 text-[12px] text-ink-500">Completa los campos para guardar.</p>
    );
  }
  return null;
}
